/**
 * coaching-messages.ts : Catalogue centralisé de tous les messages coaching in-app
 *
 * Source unique pour les banners contextuels, tooltips éducatifs et toasts de victoire.
 * Modifier ce fichier pour ajuster le ton, le contenu ou ajouter de nouveaux messages.
 */

import type { JuliettePanelTab } from "@/hooks/use-juliette-launcher";
import type { NudgeScope } from "./guidance-nudge-select";

// ─── Types ────────────────────────────────────────────────────────────────────

export type InfoTipKey =
  | "heat_score"
  | "pilot_mode"
  | "sequence_step"
  | "entry_campaign"
  | "stage_prospect"
  | "stage_engage"
  | "stage_rdv"
  | "stage_mandate"
  | "stage_clos";

export type VictoryToastKey =
  | "campaign_activated"
  | "template_created"
  | "template_updated"
  | "sequence_created"
  | "contact_enrolled";

// ─── Structures ───────────────────────────────────────────────────────────────

interface InfoTipMessage {
  message: string;
}

interface VictoryToastMessage {
  title: string;
  description: string;
}

// ─── Tooltips éducatifs ───────────────────────────────────────────────────────

export const INFO_TIPS: Record<InfoTipKey, InfoTipMessage> = {
  heat_score: {
    message:
      "Score calculé par l'IA selon les intentions du contact. 🔥 Chaud = prêt à agir, contactez-le ! ☀️ Tiède = intéressé, à nurturer. ❄️ Froid = à qualifier.",
  },
  pilot_mode: {
    message:
      "Mode IA : l'assistant envoie les messages automatiquement. Mode Humain : vous reprenez la main. Basculez à tout moment.",
  },
  sequence_step: {
    message:
      "Chaque étape = 1 message envoyé automatiquement après le délai indiqué. Le template définit le contenu.",
  },
  entry_campaign: {
    message:
      "La porte d'entrée détermine comment ce contact est arrivé et quelle séquence il reçoit en premier. Elle ne change jamais.",
  },
  stage_prospect: {
    message: "Prospect : premier contact établi, pas encore de réponse.",
  },
  stage_engage: {
    message: "Engagé : le lead a répondu ou montré de l'intérêt.",
  },
  stage_rdv: {
    message: "RDV : un rendez-vous est planifié ou réalisé.",
  },
  stage_mandate: {
    message: "Mandat : le mandat de vente est signé. Bravo !",
  },
  stage_clos: {
    message: "Clos : la transaction est finalisée. Mission accomplie !",
  },
};

// ─── Victory toasts ──────────────────────────────────────────────────────────

const VICTORY_TOASTS: Record<VictoryToastKey, VictoryToastMessage> = {
  campaign_activated: {
    title: "C'est parti ! 🚀",
    description:
      "Votre campagne est activée. Les messages seront envoyés automatiquement.",
  },
  template_created: {
    title: "Bravo ! ✨",
    description:
      "Template créé avec succès. Il est prêt pour vos séquences.",
  },
  template_updated: {
    title: "Mis à jour ! ✅",
    description: "Votre template est enregistré.",
  },
  sequence_created: {
    title: "Nouvelle séquence ! 🎯",
    description:
      "Votre séquence est prête. Activez-la pour commencer.",
  },
  contact_enrolled: {
    title: "Contact inscrit ! 📬",
    description:
      "Il recevra le premier message dans quelques heures.",
  },
};

/**
 * Retourne le payload pour un toast de victoire.
 * Utilisable directement avec `toast(victoryToast("key"))`.
 */
export function victoryToast(key: VictoryToastKey): {
  title: string;
  description: string;
} {
  return VICTORY_TOASTS[key];
}

// ─── Conseils d'accompagnement (« coups de pouce » dégressifs) ────────────────
//
// Aide de DÉBUT DE VIE, orientée présence + action (« tu n'es pas seul », « pense
// à Juliette », « remplis tes Documents »), JAMAIS pédagogie métier (ça reste le
// rôle de Juliette + des InfoTip). La bulle émane de l'orbe Juliette. Sélection :
// `guidance-nudge-select.selectNextNudge`. Extinction : « Ne plus voir » par
// conseil + interrupteur global (cf. guidance-nudge-storage).

export type GuidanceNudgeKey =
  | "ask_juliette_ambient"
  | "dashboard_first_visit"
  | "documents_attachments"
  | "crm_first_visit"
  | "messages_first_visit"
  | "scraping_zones"
  | "vitrine_first_visit";

/** Action du bouton du conseil (optionnelle). */
export type GuidanceNudgeCta =
  | { label: string; kind: "navigate"; to: string }
  | { label: string; kind: "juliette"; tab?: JuliettePanelTab; prefill?: string };

export interface GuidanceNudge {
  key: GuidanceNudgeKey;
  /** "route" = au passage sur une page (contextuel) ; "ambient" = rythme léger partout. */
  scope: NudgeScope;
  /** Base de route à matcher (requis si scope "route"), ex. "/crm". "/" = dashboard seul. */
  routeMatch?: string;
  /**
   * Une ou plusieurs cartes courtes, à la 1re personne (la voix de Juliette).
   * Plusieurs entrées = la bulle affiche une flèche « suivant » + des points de
   * progression. Le CTA n'apparaît que sur la dernière carte.
   */
  steps: string[];
  /** Bouton d'action facultatif, montré sur la dernière carte. */
  cta?: GuidanceNudgeCta;
  /** Plus grand = préféré parmi les conseils éligibles. */
  priority?: number;
}

export const GUIDANCE_NUDGES: Record<GuidanceNudgeKey, GuidanceNudge> = {
  ask_juliette_ambient: {
    key: "ask_juliette_ambient",
    scope: "ambient",
    steps: [
      "Un doute, une question ? Je suis là : je rédige un mail, j'analyse un contact ou je prépare ta journée.",
      "Clique sur mon orbe quand tu veux, partout dans l'app. Je garde le contexte de ta page.",
    ],
    cta: { label: "Parler à Juliette", kind: "juliette", tab: "echange" },
  },
  dashboard_first_visit: {
    key: "dashboard_first_visit",
    scope: "route",
    routeMatch: "/",
    steps: [
      "Ton tableau de bord réunit l'essentiel du jour : tes priorités, tes RDV et tes nouveaux leads.",
      "Les alertes sont ta liste d'actions du jour, déjà triée par priorité. Commence par le haut.",
      "Beaucoup d'un coup ? C'est normal au début. Fais les plus urgentes, le reste attendra.",
    ],
    cta: {
      label: "Par où je commence ?",
      kind: "juliette",
      tab: "echange",
      prefill: "Regarde mon tableau de bord : par quoi je commence aujourd'hui ?",
    },
  },
  documents_attachments: {
    key: "documents_attachments",
    scope: "route",
    routeMatch: "/media",
    steps: [
      "Tes Documents : range ici tes fichiers (mandats, plaquettes, photos, bons de visite…).",
      "Une fois déposés, ils deviennent des pièces jointes prêtes à glisser dans tes mails, en un clic.",
    ],
    cta: {
      label: "Comment faire ?",
      kind: "juliette",
      tab: "echange",
      prefill: "Comment j'ajoute un document pour l'envoyer en pièce jointe dans mes mails ?",
    },
  },
  crm_first_visit: {
    key: "crm_first_visit",
    scope: "route",
    routeMatch: "/crm",
    steps: [
      "Ton CRM réunit tous tes contacts. Je veille sur les plus chauds et te dis qui relancer.",
      "Clique une fiche pour tout savoir d'un contact : histoire, budget, zone, et ma reco du moment.",
      "Du nouveau après un appel ou une visite ? Dis-le-moi, je mets sa fiche à jour.",
    ],
    cta: {
      label: "Parler à Juliette",
      kind: "juliette",
      tab: "echange",
      prefill: "J'ai du nouveau sur un contact, aide-moi à mettre sa fiche à jour.",
    },
  },
  messages_first_visit: {
    key: "messages_first_visit",
    scope: "route",
    routeMatch: "/messages",
    steps: [
      "Ta messagerie réunit WhatsApp et e-mail au même endroit, tes prospects regroupés.",
      "Une réponse à écrire ? Je te prépare un brouillon calibré au contact, tu n'as plus qu'à valider.",
    ],
    cta: {
      label: "Demander à Juliette",
      kind: "juliette",
      tab: "echange",
      prefill: "Aide-moi à répondre à ce message.",
    },
  },
  scraping_zones: {
    key: "scraping_zones",
    scope: "route",
    routeMatch: "/scraping",
    steps: [
      "Le Sniper Web te remonte les annonces de vendeurs à prospecter dans ton secteur.",
      "Affine ta zone pour ne recevoir que ce qui est chez toi : moins de bruit, plus de vrais leads.",
    ],
    cta: { label: "Régler mes zones", kind: "navigate", to: "/scraping?tab=zones" },
  },
  vitrine_first_visit: {
    key: "vitrine_first_visit",
    scope: "route",
    routeMatch: "/vitrine",
    steps: [
      "Ta vitrine est ton site qui te crédibilise auprès des vendeurs et capte des leads pour toi.",
      "Je peux t'aider à la remplir et à publier tes premières pages.",
    ],
    cta: {
      label: "Demander à Juliette",
      kind: "juliette",
      tab: "echange",
      prefill: "Aide-moi à remplir ma vitrine.",
    },
  },
};

/** Liste passée à `selectNextNudge` (structurellement compatible NudgeCandidate). */
export const GUIDANCE_NUDGE_CANDIDATES: readonly GuidanceNudge[] =
  Object.values(GUIDANCE_NUDGES);
